/**
 * Shared heuristics for telling legacy (TinyMCE-authored) HTML apart from the
 * Markdown source the editors now produce.
 *
 * Mirrors the backend `MarkdownService::looksLikeHtml()` / `::isMarkdown()` so
 * the read path (`gf-markdown-viewer`), the write path (`gf-markdown-editor`)
 * and the server-side renderer all classify a stored value the same way.
 */

const STRUCTURAL_HTML_PATTERNS = [
    /<p[\s>]/i,
    /<div[\s>]/i,
    /<h[1-6][\s>]/i,
    /<table[\s>]/i,
    /<[uo]l[\s>]/i,
    /<blockquote[\s>]/i,
    /<section[\s>]/i,
    /<article[\s>]/i,
];

const MARKDOWN_PATTERNS = [
    /^#{1,6}\s+/m, /^\s*[-*+]\s+/m, /^\s*\d+\.\s+/m,
    /\[.+?\]\(.+?\)/, /!\[.+?\]\(.+?\)/, /```[\s\S]*?```/,
    /^\s*>\s+/m, /\*\*.+?\*\*/, /__.+?__/,
];

const HTML_PATTERNS = [/<[a-z]+[^>]*>/i, /<\/[a-z]+>/i];

/**
 * True when `content` carries structural block tags (<p>, <div>, <table>, …)
 * and does not look more like Markdown. Markdown with the odd inline tag
 * (`<kbd>`, `<sub>`) therefore stays classified as Markdown.
 */
export function looksLikeHtml(content: string): boolean {
    if (!content || !content.trim()) return false;
    if (!STRUCTURAL_HTML_PATTERNS.some((re) => re.test(content))) return false;
    // Tie-break: if it also looks more like markdown, leave it as markdown
    return !looksLikeMarkdown(content);
}

/** True when `content` carries more Markdown signals than generic HTML signals. */
export function looksLikeMarkdown(content: string): boolean {
    const mdScore = MARKDOWN_PATTERNS.reduce((n, re) => n + (re.test(content) ? 1 : 0), 0);
    const htmlScore = HTML_PATTERNS.reduce((n, re) => n + (re.test(content) ? 1 : 0), 0);
    return mdScore > htmlScore;
}
